import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import pages from "../../data/pages.json";
import {
  FaProjectDiagram,
  FaMobileAlt,
  FaGlobe,
  FaShoppingCart,
  FaCashRegister,
  FaChartBar,
  FaCalendarCheck,
  FaPenNib,
  FaShieldAlt,
  FaBolt,
  FaDatabase,
} from "react-icons/fa";
import {
  SiPython,
  SiPostgresql,
  SiReact,
  SiNodedotjs,
  SiAndroid,
  SiApple,
} from "react-icons/si";
import odooLogo from "../../assets/images/partners/odoo-logo.png";
import "./Services.css";

const odooApps = [
  { name: "Sales", tone: "blue" },
  { name: "Stock", tone: "plain" },
  { name: "Books", tone: "cyan" },
  { name: "CRM", tone: "plain" },
  { name: "HR", tone: "blue" },
  { name: "Web", tone: "plain" },
];

const services = [
  {
    id: "odoo",
    span: "lk-span-7",
    featured: true,
    icon: FaProjectDiagram,
    title: "Odoo ERP development",
    description:
      "Implementation, custom modules, integrations and data migration. Sales, inventory, accounting, CRM and HR set up around the way you work.",
    tags: ["Custom modules", "Integrations", "Migration", "Team training"],
    page: "/odoo-erp",
    visual: "odoo",
  },
  {
    id: "mobile",
    span: "lk-span-5",
    icon: FaMobileAlt,
    title: "Mobile apps",
    description:
      "iOS and Android apps for customers, staff and delivery teams. Ordering, bookings, tracking, loyalty and notifications.",
    tags: ["iOS", "Android", "Works offline"],
    page: "/mobile-app-development",
  },
  {
    id: "web",
    span: "lk-span-7",
    icon: FaGlobe,
    title: "Websites with SEO",
    description:
      "Fast, mobile-friendly websites and web apps built to be found on Google, with on-page SEO, structured data, speed tuning and analytics set up from day one.",
    tags: ["On-page SEO", "Speed tuning", "Analytics and Search Console"],
    page: "/web-development",
    visual: "serp",
  },
  {
    id: "ecommerce",
    span: "lk-span-5",
    icon: FaShoppingCart,
    title: "Online stores",
    description:
      "Online shops with a product catalog, cart, checkout and delivery options. Connect stock to Odoo or your POS and take payments through local gateways.",
    tags: ["Catalog", "Checkout", "Local payment gateways", "Stock sync"],
    page: "/ecommerce-development",
  },
  {
    id: "pos",
    span: "lk-span-4",
    icon: FaCashRegister,
    title: "POS and inventory",
    description:
      "Tablet and phone point of sale for shops, cafés and distributors, with billing, stock levels and daily reports.",
  },
  {
    id: "dashboards",
    span: "lk-span-4",
    icon: FaChartBar,
    title: "Dashboards and reporting",
    description:
      "Sales, stock and finance dashboards built from your own data, so you can see how the business is doing without spreadsheets.",
  },
  {
    id: "field",
    span: "lk-span-4",
    icon: FaCalendarCheck,
    title: "Field service and booking",
    description:
      "Job scheduling, technician apps, customer bookings and reminders for cleaning, repair and maintenance companies.",
  },
  {
    id: "design",
    span: "lk-span-4",
    icon: FaPenNib,
    title: "UI/UX design",
    description:
      "Clear screens and brand-ready designs for apps and websites, approved by you before development starts.",
  },
  {
    id: "hosting",
    span: "lk-span-4",
    icon: FaShieldAlt,
    title: "Hosting and support",
    description:
      "Domains, hosting, backups, security updates and monthly maintenance plans so your software stays online.",
  },
  {
    id: "automation",
    span: "lk-span-4",
    icon: FaBolt,
    title: "Automation",
    description:
      "Remove repeat work with scheduled reports, WhatsApp and email alerts, data collection and spreadsheet imports.",
  },
  {
    id: "backend",
    span: "lk-span-12",
    wide: true,
    icon: FaDatabase,
    title: "Backend and APIs",
    description: "Secure servers, databases and integrations that power your apps.",
    tags: [
      "REST APIs",
      "Payments",
      "SMS and email",
      "Notifications",
      "WhatsApp alerts",
    ],
  },
];

const stack = [
  { name: "Odoo", logo: odooLogo },
  { name: "Python", Icon: SiPython },
  { name: "PostgreSQL", Icon: SiPostgresql },
  { name: "React", Icon: SiReact },
  { name: "Node.js", Icon: SiNodedotjs },
  { name: "Android", Icon: SiAndroid },
  { name: "iOS", Icon: SiApple },
];

const OdooVisual = () => (
  <div className="lk-odoo-visual" aria-hidden="true">
    <div className="lk-odoo-badge">
      <img src={odooLogo} alt="" />
    </div>
    <div className="lk-odoo-apps">
      {odooApps.map((app) => (
        <span key={app.name} className={`is-${app.tone}`}>
          {app.name}
        </span>
      ))}
    </div>
  </div>
);

const PhoneVisual = () => (
  <div className="lk-mini-phone" aria-hidden="true">
    <div>
      <i style={{ width: "60%" }} />
      <i className="is-block" />
      <i className="is-block is-dim" />
      <i className="is-pill" />
    </div>
  </div>
);

const SerpVisual = () => (
  <div className="lk-serp" aria-hidden="true">
    <small>yourbusiness.lk</small>
    <strong>Your Business | What you sell in Colombo</strong>
    <p>
      A short description that tells searchers why to choose you, using the
      words they search for.
    </p>
  </div>
);

const visuals = { odoo: OdooVisual, phone: PhoneVisual, serp: SerpVisual };

const Services = () => {
  return (
    <section id="services" className="lk-section lk-services">
      <div className="lk-container">
        <motion.div
          className="lk-section-head"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div>
            <div className="lk-eyebrow">What we build</div>
            <h2 className="lk-h2">Software for the way your business runs.</h2>
          </div>
          <p className="lk-lead">
            From Odoo ERP to your first mobile app, we design it, build it and
            keep it running.
          </p>
        </motion.div>

        <div className="lk-bento">
          {services.map((service, index) => {
            const Icon = service.icon;
            const Visual = service.visual ? visuals[service.visual] : null;
            return (
              <motion.article
                key={service.id}
                className={`lk-card lk-service ${service.span}${
                  service.featured ? " is-featured" : ""
                }${service.wide ? " is-wide" : ""}`}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
              >
                <div className="lk-service-body">
                  <span className="lk-icon-tile">
                    <Icon aria-hidden="true" />
                  </span>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  {service.tags && (
                    <ul className="lk-chips">
                      {service.tags.map((tag) => (
                        <li key={tag}>{tag}</li>
                      ))}
                    </ul>
                  )}
                  {service.page && (
                    <Link to={service.page} className="lk-service-more">
                      More about {pages.services.find((p) => p.path === service.page).name}
                    </Link>
                  )}
                </div>
                {Visual && <Visual />}
              </motion.article>
            );
          })}
        </div>

        <div className="lk-stack">
          <p className="lk-stack-label">Technology we work with</p>
          <ul className="lk-stack-list">
            {stack.map(({ name, logo, Icon }) => (
              <li key={name}>
                {logo ? (
                  <img src={logo} alt="" className="lk-stack-img" />
                ) : (
                  <Icon aria-hidden="true" />
                )}
                {logo ? <span className="lk-sr">{name}</span> : <span>{name}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Services;
